// iPad / iOS Safari never blurs a focused text field when you tap a
// non-focusable area (a card, the page background, a header strip), so the
// on-screen keyboard opened by e.g. the header search box stayed up forever.
// Native apps dismiss it on an outside tap — do the same here: on a TOUCH /
// PEN press outside the focused field, blur it. Mouse input is untouched, so
// desktop behaviour is byte-identical.
//
// Opt out on any element with `data-keep-keyboard` (e.g. a field's own clear
// button, where the user expects to keep typing).

const EDITABLE_SELECTOR =
  'input, textarea, select, [contenteditable=""], [contenteditable="true"], label, [data-keep-keyboard]';

const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

function isKeyboardField(el: Element | null): el is HTMLElement {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(el.type);
  return el.isContentEditable;
}

let installed = false;

export function installTouchKeyboardDismiss() {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener(
    "pointerdown",
    (e) => {
      if (e.pointerType === "mouse") return;
      const active = document.activeElement;
      if (!isKeyboardField(active)) return;

      const target = e.target as Element | null;
      // Tapping the field itself, or another field / label (focus moves there
      // on its own and the keyboard should stay) — leave it alone.
      if (target && (active.contains(target) || target.closest(EDITABLE_SELECTOR))) {
        return;
      }
      active.blur();
    },
    { capture: true, passive: true }
  );
}
